// --- Prompt template ---
export const FLAME_PROMPT_TEMPLATE = `Create a single abstract flame artwork on a pure black background. The flame should be a luminous, flowing sculpture of light and energy -- not a literal campfire, but an artistic interpretation of fire as a living force.

This flame represents a person whose driving purpose is: {{goal}}. Their greatest strength is {{superpower}}. They want to feel {{feeling}} in their work.

Let these three qualities subtly influence the flame's character:
- The goal should shape the flame's overall form and direction (rising, spreading, spiraling, etc.)
- The superpower should influence the texture and inner patterns of the flame, creating an observable similarity in texture among flames with the same superpower. The basic textures and patterns are:
  - Empathy: gentle wave patterns
  - Strategy: sharp angular patterns
  - Resilience: sturdy columnar textures
  - Curiosity: swirling tendril patterns
  - Creativity: flowing arc patterns
  - Connection: interwoven strand textures
  - Leadership: towering spire patterns
  - Courage: bold flare textures
- The feeling should drive a distinct color element unique to each feeling within the overall color palette, ensuring it complements the flame's beauty without overwhelming it. The distinct feelings with unique color elements are:
  - Energized: vibrant orange
  - Balanced: soft green
  - Inspired: bright teal
  - Unstoppable: intense red
  - Connected: deep blue
  - Thriving: radiant yellow
  - Grounded: earthy brown
  - Purposeful: bold violet

Style guidelines:
- Vertical composition, flame centered
- Rich, saturated colors that transition and blend naturally
- The flame should feel like it has depth and dimension -- luminous layers behind layers
- No text, no words, no letters anywhere in the image
- No face, no human figure, no recognizable objects -- pure abstract flame
- The black background should be truly black (#000000) with no gradients or textures
- The flame should look like it belongs in a contemporary art gallery
- Aspect ratio: square`;

// --- Life-area classifier prompt ---
export const CLASSIFIER_SYSTEM_PROMPT = `You are a response classifier for a wellbeing conference booth experience. You classify free-text answers to "What change do you want to ignite?" into one of seven life-area categories.

## Audience Definitions

Before classifying, identify which audience the respondent belongs to. This shapes how you interpret their language.

### Conference Attendees
HR leaders, benefits professionals, CHROs, and people strategists. They use strategic, organizational language. When ambiguous, lean toward an organizational interpretation.

### Clients
Employers and organizational leaders who purchase wellbeing programs. Outcome-oriented and ROI-conscious. When ambiguous, lean toward workforce or program-outcomes interpretation.

### Broker Partners
Benefits brokers, consultants, and advisors. Market-aware and advisory. When ambiguous, lean toward advisory or market-level interpretation.

### Members
Individual employees or program enrollees. Personal, everyday language. When ambiguous, lean toward personal interpretation.

## Categories

1. **Lifestyle & Health** — Physical wellness, daily habits, sustainable ways of living and working.
2. **Mindset, Mental Health & Resilience** — Psychological wellbeing, emotional strength, cultural shifts around mental health.
3. **Growth & Development** — Learning, career advancement, skill-building, professional or personal evolution.
4. **Financial Wellbeing & Total Rewards** — Financial security, compensation equity, benefits strategy, economic empowerment.
5. **Purpose & Contribution** — Meaning, legacy, social impact, equity, systemic change.
6. **Relationships & Community** — Human connection, belonging, collaboration, strengthening relational bonds.
7. **Something Else** — Only when a response clearly does not fit any of the six categories above.

## Rules

1. Choose exactly one category based on the primary intent.
2. Use audience context to resolve ambiguity.
3. When responses span multiple categories, select the one capturing the core desire.
4. Honor the respondent's frame (subject of the verb matters).
5. Use "Something Else" sparingly — only for true outliers.
6. Do not infer beyond what is stated.
7. Flag low confidence when genuinely torn or response is very short/vague.

## Output

Respond with a JSON object:
{"category": "<category name>", "confidence": "high|medium|low", "rationale": "<one sentence>"}`;

// --- Validation limits ---
export const FREE_TEXT_MAX_LENGTH = 200;
export const FREE_TEXT_COUNTER_START = 150;
export const NAME_MAX_LENGTH = 100;
export const ORG_MAX_LENGTH = 200;
export const EMAIL_MAX_LENGTH = 254;

// --- Referral messages ---
export const REFERRAL_DISABLED_MESSAGE = 'Referrals are not available for this experience.';
export const REFERRAL_CAP_REACHED_MESSAGE = 'This referral link has reached its limit.';
export const REFERRAL_INVALID_MESSAGE = 'This referral link is not valid.';

// --- Brand copy ---
export const BRAND_COPY = {
  screens: {
    intro: {
      headline: 'Every Firestarter carries a flame.',
      subtitle: 'Three questions. One Spark. A flame that\u2019s all your own.',
      steps: [
        'Answer three quick questions',
        'Generate your unique flame',
        'Watch your flame on its journey across the board',
      ],
      button: 'Let\u2019s go',
    },
    spark: {
      stepLabel: 'The spark',
      question: 'What change do you want to ignite?',
      button: 'Next',
      placeholders: [
        'Build a culture where people actually thrive...',
        'Lead with more courage \u2014 even when it\u2019s uncomfortable...',
        'Create the kind of safety where people take real risks...',
        'Stop running on autopilot. Start living with intention...',
        'Be the leader who brings out what\u2019s already there...',
        'Build something bigger than a benefits strategy...',
        'Make well-being a priority \u2014 not an afterthought...',
        'Show up as the whole person, not just the role...',
      ],
    },
    superpower: {
      stepLabel: 'Your superpower',
      question: 'What\u2019s your superpower?',
      hint: 'The strength you bring into every room.',
      button: 'Next',
    },
    feeling: {
      stepLabel: 'The feeling',
      question: 'How do you want to feel at work?',
      hint: 'Not how things are. How they could be.',
      button: 'Next',
    },
    contact: {
      stepLabel: 'Almost there',
      intro: 'Tell us who you are, and we\u2019ll bring your flame to life.',
      consent:
        'By clicking next, you\u2019re sparking a connection with Aduro. We may reach out with updates and the occasional high-five.',
      button: 'Next',
    },
    generating: {
      headline: 'Igniting possibility',
      subtext: 'Your answers are becoming fire.',
      brandMessage:
        'At Aduro, we believe possibility ignites the moment you get bold enough to name what you actually want to change. We take that spark and build a path designed to keep it burning.',
      fallbackText: 'Great things take a moment.',
    },
    reveal: {
      headline: 'This is your fire.',
      muralCallout: 'Look up.',
      muralText: 'Your flame just joined the wall.',
      notebookPrompt: 'Collect your notebook at the Aduro booth. Your flame is waiting.',
      cta: 'Check us out at aduro.com',
      ctaUrl: 'https://www.aduro.com',
      shareLink: 'Share your flame',
    },
    share: {
      superpowerLabel: 'MY SUPERPOWER',
      feelingLabel: 'HOW I WANT TO FEEL AT WORK',
      shareMessageLabel: 'Your share message',
      copyButton: 'Copy',
      copiedButton: 'Copied',
      messageTemplate:
        'I just found my fire at {surfaceName}. Turns out my superpower is {superpower} \u2013 and what fuels me at work? Feeling {feeling}.\n\nThanks @Aduro for the experience. #FindYourFire {eventHashtag} #HumanPerformance',
    },
    paused: {
      headline: 'Our flame creators are taking a short break.',
      subtext:
        'Every great fire needs a moment to breathe. Come back shortly and we will bring your flame to life.',
      button: 'Try again',
    },
    unavailable: {
      headline: 'Submissions are not available right now.',
      subtext: 'Check back later — your flame journey will be ready soon.',
    },
  },
  wall: {
    header: 'What will you ignite?',
    idleTitle: 'What change will you ignite?',
    counterLabel: 'FIRES LIT',
    emptyHeadline: 'Every fire on this wall started with a spark.',
    emptySubtext: '',
    qrPrompt: 'Scan the QR code to add your flame',
    makeRoomText: 'A new flame emerges',
    toast: '{name} just lit a fire',
    groupingTitles: {
      superpower: "What's your superpower?",
      feeling: 'How do you want to feel at work?',
      lifeArea: 'What change will you ignite?',
    },
    footerLeft: 'Conference Board 2026',
    footerRight: 'Scan the QR code. Add your fire to the wall.',
    brandHeading: 'Find Your Fire',
    brandSubheading: 'What change will you ignite?',
    brandInstructions: ['Scan the code', 'Answer three questions', 'See your flame come to life'],
    brandPositioning: 'Mind. Body. Wellbeing.',
    brandTagline: 'Your Health and Human Performance Partner',
  },
} as const;
